import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useFirebase } from './firebase';
import useApi from './use-api';
import * as ROUTES from '../constants/routes';
import * as ROLES from '../constants/roles';

const SESSION_KEY_ACTIVE_COMPANY = 'activeCompany';

const userContext = createContext();

function useProvideUser() {
	const { authUser } = useFirebase();
	const [profile, setProfile] = useState(null);
	const [activeCompany, setActiveCompany] = useState(
		sessionStorage.getItem(SESSION_KEY_ACTIVE_COMPANY) &&
			sessionStorage.getItem(SESSION_KEY_ACTIVE_COMPANY) !== 'undefined'
			? JSON.parse(sessionStorage.getItem(SESSION_KEY_ACTIVE_COMPANY))
			: null,
	);

	const setCompany = (company) => {
		if (company === null) {
			sessionStorage.removeItem(SESSION_KEY_ACTIVE_COMPANY);
		} else {
			sessionStorage.setItem(SESSION_KEY_ACTIVE_COMPANY, JSON.stringify(company));
		}
		setActiveCompany(company);
	};

	const [{ data: profileData }, fetchProfile] = useApi.get(ROUTES.PROFILE, {}, { lazy: true });

	useEffect(() => {
		if (authUser && !profile) {
			fetchProfile();
		}
		if (!authUser) {
			setProfile(null);
		}
	}, [authUser, profile]);

	useEffect(() => {
		if (profileData) {
			setProfile(profileData);
			if (profileData?.companies?.length) {
				if (!activeCompany) {
					setCompany(profileData.companies[0]);
				} else {
					setCompany(profileData.companies.find((company) => company.id === activeCompany.id));
				}
			}
		}
	}, [profileData, activeCompany]);

	const { isPortalAdmin, isCompanyAdmin, isCompanySuperUser, isSchoolAdmin, isSchoolSuperUser } =
		useMemo(() => {
			if (profile?.roles) {
				return {
					isPortalAdmin: profile.roles.includes(ROLES.ADMIN),
					isCompanyAdmin: profile.roles.includes(ROLES.COMPANY_ADMIN),
					isCompanySuperUser: profile.roles.includes(ROLES.COMPANY_SUPER_USER),
					isSchoolAdmin: profile.roles.includes(ROLES.SCHOOL_ADMIN),
					isSchoolSuperUser: profile.roles.includes(ROLES.SCHOOL_SUPER_USER),
				};
			}

			return {
				isPortalAdmin: false,
				isCompanyAdmin: false,
				isCompanySuperUser: false,
				isSchoolAdmin: false,
				isSchoolSuperUser: false,
			};
		}, [profile?.roles]);

	const getFirstName = () =>
		isSchoolAdmin || isSchoolSuperUser ? profile.firstName : profile.companyContactPerson.firstName;

	return {
		isPortalAdmin,
		isCompanyAdmin,
		isCompanySuperUser,
		isSchoolAdmin,
		isSchoolSuperUser,
		activeCompany,
		setCompany,
		profile: profile || {},
		getFirstName,
		refetchProfile: () => fetchProfile(),
	};
}

export function ProvideUser({ children }) {
	const user = useProvideUser();
	return <userContext.Provider value={user}>{children}</userContext.Provider>;
}

export const useUser = () => useContext(userContext);
